import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Label } from './ui/label';
import { 
  Shield, Users, FileText, Briefcase, Calendar, BarChart3, 
  Plus, Edit, Trash2, Eye, CheckCircle, Clock, XCircle, 
  LogOut, Search, Filter, Download, Upload, Settings,
  Building2, PoundSterling, TrendingUp, Target, MapPin, Star, Instagram
} from 'lucide-react';
import axios from 'axios';
import EnhancedBlogEditor from './EnhancedBlogEditor';
import CreateCaseStudyBlog from './CreateCaseStudyBlog';
import InstagramPostManager from './InstagramPostManager';
import AdminRetailerOnboarding from './AdminRetailerOnboarding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dashboard Stats Component
const DashboardStats = ({ stats }) => {
  const statItems = [
    { label: 'Total Applications', value: stats?.total_applications || 0, icon: FileText, color: 'bg-blue-500' },
    { label: 'Pending Reviews', value: stats?.pending_applications || 0, icon: Clock, color: 'bg-orange-500' },
    { label: 'Active Businesses', value: stats?.active_businesses || 0, icon: Building2, color: 'bg-green-500' },
    { label: 'Total Jobs', value: stats?.total_jobs || 0, icon: Briefcase, color: 'bg-purple-500' },
    { label: 'Total Events', value: stats?.total_events || 0, icon: Calendar, color: 'bg-indigo-500' },
    { label: 'Blog Posts', value: stats?.total_blog_posts || 0, icon: FileText, color: 'bg-pink-500' },
    { label: 'Total Revenue', value: `£${(stats?.total_revenue || 0).toLocaleString()}`, icon: PoundSterling, color: 'bg-emerald-500' },
    { label: 'This Week Apps', value: stats?.this_week_applications || 0, icon: TrendingUp, color: 'bg-cyan-500' }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {statItems.map((item, index) => (
        <Card key={index} className="hover:shadow-lg transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{item.label}</p>
                <p className="text-3xl font-bold text-gray-900">{item.value}</p>
              </div>
              <div className={`${item.color} p-3 rounded-lg`}>
                <item.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Applications Management Component
const ApplicationsManagement = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [showCreateListing, setShowCreateListing] = useState(false);
  const [activeView, setActiveView] = useState('applications'); // applications, create, subscriptions
  
  // Create new business listing manually
  const [newListing, setNewListing] = useState({
    business_name: '',
    main_contact: '',
    email_address: '',
    mobile_phone: '',
    address: '',
    postcode: '',
    selected_package: 'standard',
    discount_details: '',
    start_date: '',
    end_date: '',
    monthly_fee: '',
    payment_status: 'pending'
  });

  const statusColors = {
    'pending': 'bg-orange-100 text-orange-800',
    'under_review': 'bg-blue-100 text-blue-800',
    'approved': 'bg-green-100 text-green-800',
    'rejected': 'bg-red-100 text-red-800',
    'active': 'bg-emerald-100 text-emerald-800',
    'suspended': 'bg-red-100 text-red-800'
  };

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('access_token');  // Use same token key as AdminLoginModal
      const response = await axios.get(`${API}/business-applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (applicationId, status = statusUpdate, notes = adminNotes) => {
    try {
      const token = localStorage.getItem('access_token');  // Use same token key as AdminLoginModal
      await axios.put(`${API}/business-applications/${applicationId}/status`, null, {
        params: { status: status, admin_notes: notes },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchApplications(); // Refresh data
      setSelectedApp(null);
      setStatusUpdate('');
      setAdminNotes('');
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full"></div></div>;
  }

  const createBusinessListing = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/admin/business-listings`, newListing, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reset form and refresh
      setNewListing({
        business_name: '', main_contact: '', email_address: '',
        mobile_phone: '', address: '', postcode: '',
        selected_package: 'standard', discount_details: '',
        start_date: '', end_date: '', monthly_fee: '', payment_status: 'pending'
      });
      setActiveView('applications');
      fetchApplications();
    } catch (error) {
      console.error('Error creating business listing:', error);
    }
  };

  const activateAccount = async (applicationId) => {
    await updateApplicationStatus(applicationId, 'active', 'Account activated by admin');
  };

  const suspendAccount = async (applicationId) => {
    await updateApplicationStatus(applicationId, 'suspended', 'Account suspended by admin');
  };

  // Function already exists above, no need to duplicate

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
          <Button
            variant={activeView === 'applications' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveView('applications')}
            className="px-4"
          >
            <FileText className="h-4 w-4 mr-2" />
            All Applications
          </Button>
          <Button
            variant={activeView === 'create' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveView('create')}
            className="px-4"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Listing
          </Button>
          <Button
            variant={activeView === 'admin-retailer' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveView('admin-retailer')}
            className="px-4 bg-green-600 hover:bg-green-700 text-white"
            style={{ backgroundColor: activeView === 'admin-retailer' ? '#7AC943' : undefined }}
          >
            <Plus className="h-4 w-4 mr-2" />
            ➕ Create Retailer Listing
          </Button>
          <Button
            variant={activeView === 'subscriptions' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveView('subscriptions')}
            className="px-4"
          >
            <PoundSterling className="h-4 w-4 mr-2" />
            Subscriptions
          </Button>
        </div>
        <Button onClick={fetchApplications} variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Applications View */}
      {activeView === 'applications' && (
        <div className="grid gap-4">
          {applications.map((app) => (
            <Card key={app.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4">
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900">{app.business_name}</h3>
                        <p className="text-gray-600">{app.main_contact} • {app.email_address}</p>
                        <p className="text-sm text-gray-500">{app.address}, {app.postcode}</p>
                      </div>
                    </div>
                    
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Package:</span> {app.selected_package || 'Not selected'}
                      </div>
                      <div>
                        <span className="font-medium">URL:</span> {app.generated_url}
                      </div>
                      <div>
                        <span className="font-medium">Applied:</span> {new Date(app.created_at).toLocaleDateString()}
                      </div>
                      <div>
                        <span className="font-medium">Phone:</span> {app.mobile_phone}
                      </div>
                    </div>

                    {app.discount_details && (
                      <div className="mt-2">
                        <span className="font-medium text-sm">Discounts:</span> 
                        <span className="text-sm text-gray-600 ml-2">{app.discount_details}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <Badge className={statusColors[app.application_status] || 'bg-gray-100 text-gray-800'}>
                      {app.application_status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    
                    {/* Quick Action Buttons */}
                    {app.application_status === 'approved' && (
                      <Button 
                        size="sm" 
                        onClick={() => activateAccount(app.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Activate
                      </Button>
                    )}
                    
                    {app.application_status === 'active' && (
                      <Button 
                        size="sm" 
                        onClick={() => suspendAccount(app.id)}
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Suspend
                      </Button>
                    )}
                    
                    <Button 
                      size="sm" 
                      onClick={() => setSelectedApp(app)}
                      variant="outline"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Manage
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Listing View */}
      {activeView === 'create' && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Business Listing</CardTitle>
            <p className="text-gray-600">Manually create a business listing for Elite packages or special campaigns</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Business Name *</Label>
                <Input
                  value={newListing.business_name}
                  onChange={(e) => setNewListing(prev => ({...prev, business_name: e.target.value}))}
                  placeholder="Business name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Main Contact *</Label>
                <Input
                  value={newListing.main_contact}
                  onChange={(e) => setNewListing(prev => ({...prev, main_contact: e.target.value}))}
                  placeholder="Contact person name"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={newListing.email_address}
                  onChange={(e) => setNewListing(prev => ({...prev, email_address: e.target.value}))}
                  placeholder="business@example.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={newListing.mobile_phone}
                  onChange={(e) => setNewListing(prev => ({...prev, mobile_phone: e.target.value}))}
                  placeholder="Phone number"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label>Address *</Label>
                <Input
                  value={newListing.address}
                  onChange={(e) => setNewListing(prev => ({...prev, address: e.target.value}))}
                  placeholder="Full address"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Postcode *</Label>
                <Input
                  value={newListing.postcode}
                  onChange={(e) => setNewListing(prev => ({...prev, postcode: e.target.value}))}
                  placeholder="Postcode"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Package Type *</Label>
                <Select value={newListing.selected_package} onValueChange={(value) => setNewListing(prev => ({...prev, selected_package: value}))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard (£15/month)</SelectItem>
                    <SelectItem value="premium">Premium (£45/month)</SelectItem>
                    <SelectItem value="elite">Elite (£500/month)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Campaign Start Date</Label>
                <Input
                  type="date"
                  value={newListing.start_date}
                  onChange={(e) => setNewListing(prev => ({...prev, start_date: e.target.value}))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Campaign End Date</Label>
                <Input
                  type="date"
                  value={newListing.end_date}
                  onChange={(e) => setNewListing(prev => ({...prev, end_date: e.target.value}))}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Discount Details</Label>
              <Textarea
                value={newListing.discount_details}
                onChange={(e) => setNewListing(prev => ({...prev, discount_details: e.target.value}))}
                placeholder="Describe the discounts and offers..."
                rows={3}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Monthly Fee (£)</Label>
                <Input
                  type="number"
                  value={newListing.monthly_fee}
                  onChange={(e) => setNewListing(prev => ({...prev, monthly_fee: e.target.value}))}
                  placeholder="Monthly subscription fee"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Payment Status</Label>
                <Select value={newListing.payment_status} onValueChange={(value) => setNewListing(prev => ({...prev, payment_status: value}))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <Button onClick={createBusinessListing} className="bg-green-600 hover:bg-green-700">
                <Plus className="h-4 w-4 mr-2" />
                Create Listing
              </Button>
              <Button variant="outline" onClick={() => setActiveView('applications')}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Retailer Onboarding View */}
      {activeView === 'admin-retailer' && (
        <AdminRetailerOnboarding />
      )}

      {/* Subscriptions View */}
      {activeView === 'subscriptions' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Subscription Management</CardTitle>
              <p className="text-gray-600">Track subscription payments and campaign dates for all business listings</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {applications.filter(app => app.application_status === 'active' || app.application_status === 'approved').map((app) => (
                  <div key={app.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">{app.business_name}</h4>
                        <p className="text-gray-600 text-sm">{app.main_contact} • {app.email_address}</p>
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="font-medium">Package:</span> {app.selected_package}
                          </div>
                          <div>
                            <span className="font-medium">Monthly Fee:</span> £{app.monthly_fee || '15'}
                          </div>
                          <div>
                            <span className="font-medium">Start Date:</span> {app.start_date || 'Not set'}
                          </div>
                          <div>
                            <span className="font-medium">End Date:</span> {app.end_date || 'Ongoing'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className={app.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {app.payment_status || 'Pending'}
                        </Badge>
                        <Button size="sm" variant="outline">
                          <Edit className="h-4 w-4 mr-1" />
                          Update
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Status Update Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <CardTitle>Manage Application: {selectedApp.business_name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><strong>Contact:</strong> {selectedApp.main_contact}</div>
                <div><strong>Email:</strong> {selectedApp.email_address}</div>
                <div><strong>Phone:</strong> {selectedApp.mobile_phone}</div>
                <div><strong>Package:</strong> {selectedApp.selected_package}</div>
              </div>
              
              <div>
                <Label>Update Status</Label>
                <Select value={statusUpdate} onValueChange={setStatusUpdate}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select new status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="chatbot_created">Chatbot Created</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Admin Notes</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this application..."
                  className="mt-1"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <Button 
                  onClick={() => updateApplicationStatus(selectedApp.id, statusUpdate, adminNotes)}
                  disabled={!statusUpdate}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Update Status
                </Button>
                <Button variant="outline" onClick={() => setSelectedApp(null)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Blog Management Component
const BlogManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [showCaseStudy, setShowCaseStudy] = useState(true);
  const [editingBlog, setEditingBlog] = useState(null);

  const fetchBlogs = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/blogs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      // Use mock data for demo
      setBlogs([
        {
          id: '1',
          title: 'Getting Started with QR Discounts',
          author: 'Admin',
          excerpt: 'Learn how to implement QR code discounts in your retail business.',
          category: 'guides',
          published: true,
          is_featured: false,
          created_at: new Date().toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const saveBlog = async (blogData) => {
    try {
      const token = localStorage.getItem('access_token');
      const method = editingBlog ? 'put' : 'post';
      const url = editingBlog 
        ? `${API}/admin/blogs/${editingBlog.id}` 
        : `${API}/admin/blogs`;

      await axios[method](url, blogData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchBlogs();
      setShowEditor(false);
      setEditingBlog(null);
    } catch (error) {
      console.error('Error saving blog:', error);
      // For demo, just add to local state
      const newBlog = {
        id: Date.now().toString(),
        ...blogData,
        created_at: new Date().toISOString()
      };
      setBlogs(prev => [newBlog, ...prev]);
      setShowEditor(false);
      setEditingBlog(null);
    }
  };

  const deleteBlog = async (blogId) => {
    if (!confirm('Are you sure you want to delete this blog post?')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/admin/blogs/${blogId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBlogs();
    } catch (error) {
      console.error('Error deleting blog:', error);
      setBlogs(prev => prev.filter(blog => blog.id !== blogId));
    }
  };

  const editBlog = (blog) => {
    setEditingBlog(blog);
    setShowEditor(true);
  };

  const handleCaseStudyCreated = (blogData) => {
    saveBlog(blogData);
    setShowCaseStudy(false);
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  if (loading) return <div>Loading blogs...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Blog Management</h2>
        <div className="flex gap-3">
          <Button onClick={() => setShowEditor(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="h-4 w-4 mr-2" />
            New Blog Post
          </Button>
        </div>
      </div>

      {/* Case Study Creator */}
      {showCaseStudy && (
        <div>
          <CreateCaseStudyBlog 
            onSave={handleCaseStudyCreated}
            onClose={() => setShowCaseStudy(false)}
          />
        </div>
      )}

      {/* Blog List */}
      <div className="grid gap-4">
        {blogs.map((blog) => (
          <Card key={blog.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold">{blog.title}</h3>
                    {blog.is_featured && (
                      <Badge className="bg-amber-100 text-amber-800">
                        <Star className="h-3 w-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                    <Badge variant={blog.published ? "default" : "secondary"}>
                      {blog.published ? 'Published' : 'Draft'}
                    </Badge>
                  </div>
                  <p className="text-gray-600 mb-2">{blog.excerpt}</p>
                  <div className="text-sm text-gray-500">
                    By {blog.author} • {new Date(blog.created_at).toLocaleDateString()}
                    {blog.category && ` • ${blog.category}`}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => editBlog(blog)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteBlog(blog.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Enhanced Blog Editor */}
      <EnhancedBlogEditor
        isOpen={showEditor}
        onClose={() => {
          setShowEditor(false);
          setEditingBlog(null);
        }}
        onSave={saveBlog}
        editingBlog={editingBlog}
        initialData={editingBlog || {}}
      />
    </div>
  );
};

// Jobs Management Component  
const JobsManagement = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    job_type: 'full_time',
    location: '',
    salary_range: '',
    description: '',
    requirements: [],
    benefits: [],
    application_url: '',
    contact_email: '',
    is_featured: false,
    expires_at: ''
  });

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('access_token');  // Use same token key as AdminLoginModal
      const response = await axios.get(`${API}/admin/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveJob = async () => {
    try {
      const token = localStorage.getItem('access_token');  // Use same token key as AdminLoginModal
      const jobData = {
        ...formData,
        requirements: formData.requirements.filter(req => req.trim()),
        benefits: formData.benefits.filter(ben => ben.trim()),
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
      };
      
      const url = editingJob ? `${API}/admin/jobs/${editingJob.id}` : `${API}/admin/jobs`;
      const method = editingJob ? 'put' : 'post';
      
      await axios[method](url, jobData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchJobs();
      setShowEditor(false);
      setEditingJob(null);
      setFormData({
        title: '', company: '', job_type: 'full_time', location: '', salary_range: '',
        description: '', requirements: [], benefits: [], application_url: '',
        contact_email: '', is_featured: false, expires_at: ''
      });
    } catch (error) {
      console.error('Error saving job:', error);
    }
  };

  const deleteJob = async (jobId) => {
    if (!confirm('Are you sure you want to delete this job?')) return;
    
    try {
      const token = localStorage.getItem('access_token');  // Use same token key as AdminLoginModal
      await axios.delete(`${API}/admin/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
    }
  };

  const editJob = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      company: job.company,
      job_type: job.job_type,
      location: job.location,
      salary_range: job.salary_range || '',
      description: job.description,
      requirements: job.requirements || [''],
      benefits: job.benefits || [''],
      application_url: job.application_url || '',
      contact_email: job.contact_email || '',
      is_featured: job.is_featured,
      expires_at: job.expires_at ? new Date(job.expires_at).toISOString().split('T')[0] : ''
    });
    setShowEditor(true);
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Jobs Management</h2>
        <Button onClick={() => setShowEditor(true)} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="h-4 w-4 mr-2" />
          New Job Posting
        </Button>
      </div>

      <div className="grid gap-4">
        {jobs.map((job) => (
          <Card key={job.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-semibold text-lg">{job.title}</h3>
                    <Badge className="bg-purple-100 text-purple-800">
                      {job.job_type.replace('_', ' ').toUpperCase()}
                    </Badge>
                    {job.is_featured && <Badge className="bg-orange-100 text-orange-800">Featured</Badge>}
                  </div>
                  <p className="text-gray-600">{job.company} • {job.location}</p>
                  {job.salary_range && <p className="text-sm text-gray-500">Salary: {job.salary_range}</p>}
                  <p className="text-xs text-gray-500 mt-2">
                    Posted: {new Date(job.created_at).toLocaleDateString()}
                    {job.expires_at && ` • Expires: ${new Date(job.expires_at).toLocaleDateString()}`}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <Button size="sm" variant="outline" onClick={() => editJob(job)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteJob(job.id)} className="text-red-600 hover:text-red-700">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Job Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="max-w-4xl w-full my-8">
            <CardHeader>
              <CardTitle>{editingJob ? 'Edit Job' : 'Create New Job'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Job Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Sales Assistant"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Company</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="Company name"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Job Type</Label>
                  <Select value={formData.job_type} onValueChange={(value) => setFormData(prev => ({ ...prev, job_type: value }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full Time</SelectItem>
                      <SelectItem value="part_time">Part Time</SelectItem>
                      <SelectItem value="temporary">Temporary</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="apprenticeship">Apprenticeship</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="City, Region"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Salary Range</Label>
                  <Input
                    value={formData.salary_range}
                    onChange={(e) => setFormData(prev => ({ ...prev, salary_range: e.target.value }))}
                    placeholder="e.g., £20,000 - £25,000"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Job Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Detailed job description..."
                  rows={6}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                    placeholder="applications@company.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Expires On</Label>
                  <Input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => setFormData(prev => ({ ...prev, expires_at: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">Featured Job</span>
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button onClick={saveJob} className="bg-purple-600 hover:bg-purple-700">
                  {editingJob ? 'Update' : 'Create'} Job
                </Button>
                <Button variant="outline" onClick={() => { setShowEditor(false); setEditingJob(null); }}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Events Management Component
const EventsManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'seasonal_fair',
    location: '',
    address: '',
    date: '',
    end_date: '',
    time: '',
    organizer: '',
    contact_info: '',
    website: '',
    ticket_price: '',
    is_featured: false,
    image_url: ''
  });

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('access_token');  // Use same token key as AdminLoginModal
      const response = await axios.get(`${API}/admin/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveEvent = async () => {
    try {
      const token = localStorage.getItem('access_token');  // Use same token key as AdminLoginModal
      const eventData = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };
      
      const url = editingEvent ? `${API}/admin/events/${editingEvent.id}` : `${API}/admin/events`;
      const method = editingEvent ? 'put' : 'post';
      
      await axios[method](url, eventData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchEvents();
      setShowEditor(false);
      setEditingEvent(null);
      setFormData({
        title: '', description: '', event_type: 'seasonal_fair', location: '', address: '',
        date: '', end_date: '', time: '', organizer: '', contact_info: '', website: '',
        ticket_price: '', is_featured: false, image_url: ''
      });
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const deleteEvent = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const token = localStorage.getItem('access_token');  // Use same token key as AdminLoginModal
      await axios.delete(`${API}/admin/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const editEvent = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      event_type: event.event_type,
      location: event.location,
      address: event.address,
      date: new Date(event.date).toISOString().split('T')[0],
      end_date: event.end_date ? new Date(event.end_date).toISOString().split('T')[0] : '',
      time: event.time || '',
      organizer: event.organizer,
      contact_info: event.contact_info || '',
      website: event.website || '',
      ticket_price: event.ticket_price || '',
      is_featured: event.is_featured,
      image_url: event.image_url || ''
    });
    setShowEditor(true);
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Events Management</h2>
        <Button onClick={() => setShowEditor(true)} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="h-4 w-4 mr-2" />
          New Event
        </Button>
      </div>

      <div className="grid gap-4">
        {events.map((event) => (
          <Card key={event.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-semibold text-lg">{event.title}</h3>
                    <Badge className="bg-indigo-100 text-indigo-800">
                      {event.event_type.replace('_', ' ').toUpperCase()}
                    </Badge>
                    {event.is_featured && <Badge className="bg-orange-100 text-orange-800">Featured</Badge>}
                  </div>
                  <p className="text-gray-600 flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    {event.location} • {new Date(event.date).toLocaleDateString()}
                    {event.time && ` at ${event.time}`}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">Organized by: {event.organizer}</p>
                  {event.ticket_price && (
                    <p className="text-sm text-gray-500">Ticket Price: {event.ticket_price}</p>
                  )}
                </div>
                <div className="flex space-x-2">
                  <Button size="sm" variant="outline" onClick={() => editEvent(event)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteEvent(event.id)} className="text-red-600 hover:text-red-700">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Event Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="max-w-4xl w-full my-8">
            <CardHeader>
              <CardTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Event Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Event name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Event Type</Label>
                  <Select value={formData.event_type} onValueChange={(value) => setFormData(prev => ({ ...prev, event_type: value }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="seasonal_fair">Seasonal Fair</SelectItem>
                      <SelectItem value="local_market">Local Market</SelectItem>
                      <SelectItem value="workshop">Workshop</SelectItem>
                      <SelectItem value="festival">Festival</SelectItem>
                      <SelectItem value="promotion">Promotion</SelectItem>
                      <SelectItem value="tasting">Tasting</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Event description..."
                  rows={4}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="City, Region"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Full address"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>End Date (Optional)</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input
                    value={formData.time}
                    onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                    placeholder="e.g., 10:00 AM"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Ticket Price</Label>
                  <Input
                    value={formData.ticket_price}
                    onChange={(e) => setFormData(prev => ({ ...prev, ticket_price: e.target.value }))}
                    placeholder="Free or £10"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Organizer</Label>
                  <Input
                    value={formData.organizer}
                    onChange={(e) => setFormData(prev => ({ ...prev, organizer: e.target.value }))}
                    placeholder="Organization name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Contact Info</Label>
                  <Input
                    value={formData.contact_info}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_info: e.target.value }))}
                    placeholder="Phone or email"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">Featured Event</span>
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button onClick={saveEvent} className="bg-indigo-600 hover:bg-indigo-700">
                  {editingEvent ? 'Update' : 'Create'} Event
                </Button>
                <Button variant="outline" onClick={() => { setShowEditor(false); setEditingEvent(null); }}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const navigate = useNavigate();

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('access_token');  // Use same token key as AdminLoginModal
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');  // Use same token key as AdminLoginModal
    setCurrentUser(null);
    navigate('/admin');
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');  // Use same token key as AdminLoginModal
    if (token && !currentUser) {
      // Auto-login if token exists
      setCurrentUser({ name: 'Admin', role: 'admin' });
      fetchStats();
    }
  }, [currentUser]);

  if (!currentUser) {
    // This should not happen as authentication is handled in App.js
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-charcoal mb-4">Loading Admin Dashboard...</h2>
          <p className="text-gray-600">Please wait while we verify your session.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_be48effd-3c36-477f-be09-01d2f597ad9e/artifacts/3m4p66qd_shoppersdiscountlogo%20large-edit-20231028192141.jpg" 
                alt="Shoppers Discount Club" 
                className="h-10 w-10 rounded-full"
              />
              <div>
                <h1 className="text-xl font-bold text-green-600">Shoppers Discount Club</h1>
                <p className="text-sm text-gray-600">Admin Dashboard</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, {currentUser.name}</span>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="dashboard">
              <BarChart3 className="h-4 w-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="applications">
              <FileText className="h-4 w-4 mr-2" />
              Applications
            </TabsTrigger>
            <TabsTrigger value="blogs">
              <FileText className="h-4 w-4 mr-2" />
              Blogs
            </TabsTrigger>
            <TabsTrigger value="jobs">
              <Briefcase className="h-4 w-4 mr-2" />
              Jobs
            </TabsTrigger>
            <TabsTrigger value="events">
              <Calendar className="h-4 w-4 mr-2" />
              Events
            </TabsTrigger>
            <TabsTrigger value="instagram">
              <Instagram className="h-4 w-4 mr-2" />
              Instagram
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Dashboard Overview</h2>
                <DashboardStats stats={stats} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="applications">
            <ApplicationsManagement />
          </TabsContent>

          <TabsContent value="blogs">
            <BlogManagement />
          </TabsContent>

          <TabsContent value="jobs">
            <JobsManagement />
          </TabsContent>

          <TabsContent value="events">
            <EventsManagement />
          </TabsContent>

          <TabsContent value="instagram">
            <InstagramPostManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;